import React, { useCallback, useState, useEffect } from "react";
import { RouteComponentProps } from "react-router-dom";
import ShadowDOMWrapper from "../components/ShadowDOMWrapper";
import Cookies from "universal-cookie";
import Api from "../lib/api";

interface Props {
  surveyDefinitionUrl: "protege" | "protege-spouse";
}

const api = new Api();

export const StartNoCodeStatic: React.FC<RouteComponentProps & Props> = ({
  history,
  surveyDefinitionUrl,
}) => {
  const [cssContent, setCssContent] = useState<string>("");
  const [fontsLoaded, setFontsLoaded] = useState<boolean>(false);
  const cookies = new Cookies();
  const hasReferredBy = !!cookies.get("referred-by");

  useEffect(() => {
    // Load fonts using FontFace API (reuse same fonts as mentor version)
    const loadFonts = async () => {
      try {
        // Check if fonts are already loaded to avoid duplicates
        const existingFonts = Array.from(document.fonts).filter(
          (font) => font.family === "ABC Monument Grotesk",
        );

        if (existingFonts.length > 0) {
          setFontsLoaded(true);
          return;
        }

        const fonts = [
          new FontFace(
            "ABC Monument Grotesk",
            "url(/static-assets/fonts/ABCMonumentGrotesk-Regular.woff2)",
            { weight: "400", style: "normal" },
          ),
          new FontFace(
            "ABC Monument Grotesk",
            "url(/static-assets/fonts/ABCMonumentGrotesk-RegularItalic.woff2)",
            { weight: "400", style: "italic" },
          ),
          new FontFace(
            "ABC Monument Grotesk",
            "url(/static-assets/fonts/ABCMonumentGrotesk-Bold.woff2)",
            { weight: "700", style: "normal" },
          ),
          new FontFace(
            "ABC Monument Grotesk",
            "url(/static-assets/fonts/ABCMonumentGrotesk-BoldItalic.woff2)",
            { weight: "700", style: "italic" },
          ),
        ];

        // Add fonts to document
        fonts.forEach((font) => document.fonts.add(font));

        // Load all fonts
        await Promise.all(fonts.map((font) => font.load()));

        console.log("ABC Monument Grotesk fonts loaded successfully");
        setFontsLoaded(true);
      } catch (error) {
        console.error("Failed to load fonts:", error);
        setFontsLoaded(true); // Continue anyway with fallback fonts
      }
    };

    loadFonts();
  }, []);

  useEffect(() => {
    // Load the CSS file
    fetch("/static-assets/frontend-styles.css")
      .then((response) => response.text())
      .then((css) => {
        // Replace :root selectors with :host to work in shadow DOM
        const shadowCSS = css.replace(/:root/g, ":host");
        setCssContent(shadowCSS);
      })
      .catch((error) => console.error("Failed to load CSS:", error));
  }, []);

  const handleShadowMount = useCallback(
    (shadowRoot: ShadowRoot) => {
      const form = shadowRoot.querySelector("form");
      if (!form) return;

      form.addEventListener("submit", async (e) => {
        e.preventDefault();

        const emailInput = shadowRoot.querySelector<HTMLInputElement>("#email");
        const acceptCheckbox =
          shadowRoot.querySelector<HTMLInputElement>("#accept");
        const errorDiv =
          shadowRoot.querySelector<HTMLDivElement>("#error-message");
        const submitButton = shadowRoot.querySelector<HTMLButtonElement>(
          'button[type="submit"]',
        );

        if (!emailInput || !acceptCheckbox) return;

        // Clear previous errors
        if (errorDiv) {
          errorDiv.style.display = "none";
          errorDiv.textContent = "";
        }

        // Validation
        const email = emailInput.value.trim();
        const emailRegex = /^[^ @]+@[^ @]+\.[^ @][^ @]+$/;

        if (!email) {
          if (errorDiv) {
            errorDiv.textContent = "Please enter your email address";
            errorDiv.style.display = "block";
          }
          return;
        }

        if (!emailRegex.test(email)) {
          if (errorDiv) {
            errorDiv.textContent = "Please enter a valid email address";
            errorDiv.style.display = "block";
          }
          return;
        }

        if (!acceptCheckbox.checked) {
          if (errorDiv) {
            errorDiv.textContent = "You must accept the terms to continue";
            errorDiv.style.display = "block";
          }
          return;
        }

        // Disable submit button
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = "Processing...";
        }

        try {
          const cookies = new Cookies();
          const referredBy = cookies.get("referred-by");

          // Call API using the same method as original StartNoCode
          const newApp = {
            email,
            agree: "agree",
            surveyDefinitionUrl,
            referredBy,
          };

          const response = await api.createApplicationSendEmail(newApp);

          if (response.data.success) {
            // Navigate to survey
            history.push(`/a/${response.data.message}`);
          } else {
            if (errorDiv) {
              errorDiv.textContent =
                response.data.message ||
                "Something went wrong. Please try again.";
              errorDiv.style.display = "block";
            }
          }
        } catch (error) {
          if (errorDiv) {
            errorDiv.textContent =
              "Connection error. Please check your internet and try again.";
            errorDiv.style.display = "block";
          }
        } finally {
          // Re-enable submit button
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = "Submit";
          }
        }
      });
    },
    [history, surveyDefinitionUrl],
  );

  // Generate spouse-specific text if needed
  const spouseText =
    surveyDefinitionUrl === "protege-spouse"
      ? "Questions about your spouse's service are strictly for demographic reporting - we look forward to learning about <em>your</em> career ambitions."
      : "";

  // Generate military spouse alert for veteran applications with referred-by cookie
  const militarySpouseAlert =
    surveyDefinitionUrl === "protege" && hasReferredBy
      ? `
      <div class="rounded-md p-3 mb-3" style="background: #fef3c7; border: 1px solid #f59e0b;">
        <p class="font-bold" style="color: #92400e; margin-bottom: 8px;">Military Spouse?</p>
        <p style="color: #92400e; margin: 0;">
          This page is for Veteran applications. If you're a Military Spouse, <a href="/protege-spouse" style="color: #1e40af; text-decoration: underline;">click here</a> for the spouse application.
        </p>
      </div>
    `
      : "";

  const notSpouseAlert =
    surveyDefinitionUrl === "protege-spouse" && hasReferredBy
      ? `
      <div class="rounded-md p-3 mb-3" style="background: #fef3c7; border: 1px solid #f59e0b;">
        <p class="font-bold" style="color: #92400e; margin-bottom: 8px;">Not a Spouse?</p>
        <p style="color: #92400e; margin: 0;">
          This page is for Military Spouse applications. If you're a Veteran, <a href="/protege" style="color: #1e40af; text-decoration: underline;">click here</a> for the Veteran application.
        </p>
      </div>
    `
      : "";

  // Conditional hero image and headline based on survey type
  const heroImageBase =
    surveyDefinitionUrl === "protege-spouse" ? "spouse-hero" : "protege-hero";

  const heroPicture = `
    <picture>
      <source media="(max-width: 640px)" srcset="/static-assets/${heroImageBase}-sm.jpg" />
      <source media="(max-width: 1280px)" srcset="/static-assets/${heroImageBase}-md.jpg" />
      <img src="/static-assets/${heroImageBase}.jpg" alt="${surveyDefinitionUrl === "protege-spouse" ? "Military Spouse" : "Veteran"} Image" class="h-full w-full object-cover" />
    </picture>
  `;

  const headline =
    surveyDefinitionUrl === "protege-spouse"
      ? "Navigate your career with confidence." // Using same headline for now, will be updated when spouse headline is available
      : "Your next mission starts here.";

  const htmlContent = `
    <body class="wp-singular page-template page-template-template-apply-veteran-spouse page-template-template-apply-veteran-spouse-php page page-id-6056 wp-embed-responsive wp-theme-american-corporate-partners test-apply-veterans-spouses">
    <div class="lg:flex">
      <div class="relative flex w-full items-end min-h-35 lg:min-h-none lg:h-screen lg:sticky lg:top-0">
        <div class="lg:relative flex h-full w-full items-end p-3 lg:p-5">
          <div class="absolute top-3 left-3 z-5 inline-flex grow-0 basis-1/2 shrink-1 max-w-9 lg:max-w-12 lg:top-5 lg:left-5">
            <a data-header-logo class="inline-flex w-full object-contain" href="/">
              <img src="/logo-white.svg" alt="American Corporate Partners" />
              <span class="sr-only">Go to home page</span>
            </a>
          </div>
          <h1 class="relative text-white max-w-[15ch] z-5">
            ${headline}
          </h1>
          <div class="absolute inset-0">
            ${heroPicture}
            <div class="pointer-events-none absolute bottom-0 left-0 h-full w-full z-1" style="background: linear-gradient(to left, transparent, rgba(0,0,0,0.5))"></div>
          </div>
        </div>
      </div>
      <div class="bg-white p-3 lg:p-5 lg:max-w-50">
        <div class="w-full">
          <h2 class="text-lg text-brave-blue">Get started</h2>
          <p class="text-deemphasized">
            It's easy to apply for ACP mentorship, and it's completely free. To get started, fill out this application.
            Our application takes about 15 minutes to complete. ${spouseText ? ` ${spouseText}` : ""}
          </p>

          ${militarySpouseAlert}
          ${notSpouseAlert}
           
          <div class="rounded-md p-3 text-deemphasized bg-light-grey">
            <p class="font-bold text-brave-blue">Enter your email address to begin</p>
            <div id="error-message" style="display: none; background: #fed7d7; color: #c53030; padding: 12px; border-radius: 4px; margin-bottom: 16px;"></div>
            <form>
              <div class="flex flex-col gap-3">
                <label for="email" class="sr-only">Email address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Email address"
                  required
                  class="rounded-md bg-white p-1 text-sm leading-none transition-colors duration-300 focus-visible:outline-3 focus-visible:outline-brave-blue/40 text-brave-blue focus-visible:outline-offset-2"
                />
                <div class="flex items-start gap-1">
                  <input type="checkbox" id="accept" name="accept" required />
                  <label for="accept" class="relative -top-[0.35em] text-balance">
                    I accept that ACP staff will use some or all of the information provided in this application as a means
                    of introduction to my Mentor.
                  </label>
                </div>
                <div class="block">
                  <button type="submit" class="button button-bright-blue">Submit</button>
                </div>
              </div>
            </form>
          </div>

          <div class="mb-3 rounded-md p-3 mt-1.5 bg-light-grey">
            <h2 class="text-base text-brave-blue">Questions?</h2>
            <p class="text-deemphasized max-w-[28ch]">
              Send us an email at <a href="mailto:info@acp-usa.org">info@acp-usa.org</a> or give us a call at (212) 752-0700.
            </p>
          </div>
        </div>
      </div>
    </div>
    </body>
  `;

  // Wait for both CSS and fonts to load before rendering
  if (!cssContent || !fontsLoaded) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "white",
        }}
      >
        <style>
          {`
            @keyframes pulse {
              0%, 100% {
                transform: scale(1);
                opacity: 1;
              }
              50% {
                transform: scale(1.05);
                opacity: 0.9;
              }
            }
            .loading-logo {
              animation: pulse 1.5s ease-in-out infinite;
            }
          `}
        </style>
        <img
          src="/logo-2019.color.whitebg.small.png"
          alt="American Corporate Partners"
          className="loading-logo"
          style={{
            width: "200px",
            height: "auto",
          }}
        />
        <p style={{ marginTop: "20px", color: "#666", fontSize: "14px" }}>
          Loading...
        </p>
      </div>
    );
  }

  return (
    <ShadowDOMWrapper
      html={htmlContent}
      styles={cssContent}
      onMount={handleShadowMount}
    />
  );
};

export default StartNoCodeStatic;
